import type { ActiveModal } from './modals';
import type { AdFlowPort, LevelFlowPort } from './ports';

export type FailureModal = 'none' | 'result';

export interface LevelFlowDeps {
  readonly isDev: boolean;
  readonly getLocationHref: () => string;
  readonly loadCurrentLevel: () => number;
  readonly saveCurrentLevel: (levelNumber: number) => number;
  readonly clampLevelNumber: (levelNumber: number) => number;
  readonly maximumLevelNumber: number;
  readonly ensureLevelLoaded: (levelNumber: number) => Promise<unknown>;
  readonly adFlow: AdFlowPort;
  readonly emitSetLevel: (levelNumber: number) => void;
  readonly sync: () => void;
  readonly restoreResultActions: (isFinalLevel: boolean) => void;
  readonly setActiveModal: (modal: ActiveModal) => void;
  readonly dispatchRestart: () => void;
  readonly attemptBegan: (levelNumber: number) => void;
  readonly reportLoadError?: (levelNumber: number, error: unknown) => void;
}

export interface LevelFlowApi extends LevelFlowPort {
  readonly resultPending: boolean;
  initialize(): Promise<void>;
  loadLevel(levelNumber: number, failureModal?: FailureModal): Promise<void>;
  restartGame(): void;
  replayCurrentLevel(): Promise<void>;
  markResultPending(): void;
  destroy(): void;
}

export function createLevelFlow(deps: LevelFlowDeps): LevelFlowApi {
  const savedLevel = deps.loadCurrentLevel();
  let currentLevel = savedLevel;
  if (deps.isDev) {
    const levelParam = new URL(deps.getLocationHref()).searchParams.get('level');
    const requestedLevel = levelParam === null ? Number.NaN : Number(levelParam);
    if (Number.isInteger(requestedLevel)) {
      currentLevel = deps.clampLevelNumber(requestedLevel);
    }
  }

  let epoch = 0;
  let resultPending = false;
  const reportLoadError = deps.reportLoadError
    ?? ((levelNumber: number, error: unknown) => {
      console.error(`Failed to load level ${levelNumber}.`, error);
    });

  const loadLevel = async (
    levelNumber: number,
    failureModal: FailureModal = 'none',
  ): Promise<void> => {
    const target = deps.clampLevelNumber(levelNumber);
    const loadEpoch = ++epoch;
    deps.adFlow.resetTransientState();
    try {
      await deps.ensureLevelLoaded(target);
      if (loadEpoch !== epoch) {
        return;
      }
      currentLevel = target;
      resultPending = false;
      deps.attemptBegan(currentLevel);
      deps.emitSetLevel(currentLevel);
      deps.sync();
    } catch (error) {
      if (loadEpoch !== epoch) {
        return;
      }
      reportLoadError(target, error);
      resultPending = false;
      deps.sync();
      deps.restoreResultActions(currentLevel === deps.maximumLevelNumber);
      if (failureModal === 'result') {
        deps.setActiveModal('result');
      }
    }
  };

  const restartGame = (): void => {
    epoch += 1;
    deps.attemptBegan(currentLevel);
    deps.dispatchRestart();
  };

  return {
    get epoch() {
      return epoch;
    },
    get currentLevel() {
      return currentLevel;
    },
    get resultPending() {
      return resultPending;
    },
    initialize: async () => {
      await deps.ensureLevelLoaded(currentLevel);
      deps.attemptBegan(currentLevel);
    },
    loadLevel,
    loadNextLevel: () => loadLevel(deps.loadCurrentLevel(), 'result'),
    restartAfterAd: restartGame,
    // AdsService / AdFlowApi を受け取らないため、通常の restart / replay は構造的に広告なし。
    restartGame,
    replayCurrentLevel: () => loadLevel(deps.saveCurrentLevel(currentLevel)),
    markResultPending: () => {
      resultPending = true;
    },
    destroy: () => {},
  };
}
